import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Menu, X } from 'lucide-react';
import { FONT_SANS } from '../constants';
import { colors } from '../theme/colors';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import { LinkPrimary } from './Button';
import { BRAND_LOGO_SRC, LogoImage, LogoLink } from './BrandLogo';

const HeaderWrap = styled.header`
  position: fixed;
  inset: 0 0 auto;
  z-index: 1000;
  pointer-events: none;
`;

const Nav = styled.nav`
  margin: 12px clamp(12px, 3vw, 24px) 0;
  pointer-events: auto;
`;

const NavBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  padding: 0 8px 0 16px;
  background: transparent;
`;

const Logo = LogoLink;

const DesktopMenu = styled.div`
  display: none;
  gap: 4px;
  align-items: center;

  @media (min-width: 768px) {
    display: flex;
  }

  a:not([data-cta]) {
    font-family: ${FONT_SANS};
    font-size: 14px;
    font-weight: 600;
    color: ${colors.inkSoft};
    padding: 8px 12px;
    border-radius: 8px;
    text-decoration: none;
    transition: color 0.15s, background 0.15s;

    &:hover {
      color: ${colors.ink};
      background: rgba(44, 42, 41, 0.05);
    }
  }
`;

const HeaderCta = styled(LinkPrimary)`
  padding: 8px 16px;
  font-size: 14px;
  margin-left: 4px;
`;

const Toggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: ${colors.inkSoft};
  cursor: pointer;

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileMenu = styled.div`
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  background: #fff;
  border-radius: 14px;
  margin-top: 8px;
  padding: 8px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  border: 1px solid ${colors.border};

  @media (min-width: 768px) {
    display: none !important;
  }

  a {
    display: block;
    font-family: ${FONT_SANS};
    font-size: 15px;
    font-weight: 600;
    color: ${colors.inkSoft};
    padding: 12px 14px;
    border-radius: 8px;
    text-decoration: none;

    &:hover {
      background: ${colors.bg};
    }
  }
`;

const MobileLang = styled.div`
  @media (min-width: 768px) {
    display: none;
  }
`;

function LandingHeader() {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();

  const navItems = [
    { label: t.nav.features, to: '/#events' },
    { label: t.nav.premium, to: '/#premium' },
    { label: t.nav.roadmap, to: '/#roadmap' },
    { label: t.nav.about, to: '/about' },
    { label: t.nav.support, to: '/support' },
  ];

  return (
    <HeaderWrap>
      <Nav aria-label="Primary">
        <NavBar>
          <Logo to="/" aria-label="omw home">
            <LogoImage src={BRAND_LOGO_SRC} alt="omw" $size={34} />
          </Logo>
          <DesktopMenu>
            {navItems.map((item) => (
              <Link key={item.to} to={item.to}>
                {item.label}
              </Link>
            ))}
            <LanguageSwitcher />
            <HeaderCta to="/support" data-cta>
              {t.nav.testflight}
            </HeaderCta>
          </DesktopMenu>
          <MobileLang>
            <LanguageSwitcher compact />
          </MobileLang>
          <Toggle
              type="button"
              aria-expanded={open}
              aria-label={open ? t.a11y.closeMenu : t.a11y.openMenu}
              onClick={() => setOpen((v) => !v)}
            >
            {open ? <X size={22} /> : <Menu size={22} />}
          </Toggle>
        </NavBar>
        <MobileMenu $open={open}>
          {navItems.map((item) => (
            <Link key={item.to} to={item.to} onClick={() => setOpen(false)}>
              {item.label}
            </Link>
          ))}
          <Link to="/support" onClick={() => setOpen(false)}>
            {t.nav.joinTestflight}
          </Link>
        </MobileMenu>
      </Nav>
    </HeaderWrap>
  );
}

export default LandingHeader;
